import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
} from '@mui/material';
import { Check, Visibility, VisibilityOff, VpnKey, Schedule } from '@mui/icons-material';
import Switch from '@mui/material/Switch';
import { useAuth } from '../../contexts/AuthContext';
import { useUpdateProfile, useUpdateOrganizationSettings } from '../../hooks/useUser';
import { api } from '../../services/api';
import TierBadge, { SubscriptionTier, UserRole, tierDisplayNames } from '../../components/common/TierBadge';
import AvatarUpload from '../../components/profile/AvatarUpload';
import ContractSettingsSection from '../../components/settings/ContractSettingsSection';
import GoogleCalendarConnect from '../../components/settings/GoogleCalendarConnect';
import { useSubscription } from '../../hooks/useSubscription';

// Tier hierarchy for feature checking
const TIER_HIERARCHY: SubscriptionTier[] = ['expired', 'free', 'trial', 'launch', 'rise', 'summit'];

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phone: z.string().optional(),
  bio: z.string().max(500).optional(),
  sessionIdleTimeout: z.number().min(15).max(240).optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

// Session Timeout Options
const SESSION_TIMEOUT_OPTIONS = [
  { value: 15, label: '15 minutes (High Security)' },
  { value: 30, label: '30 minutes (Standard)' },
  { value: 60, label: '1 hour (Extended)' },
  { value: 120, label: '2 hours (Long)' },
  { value: 240, label: '4 hours (Maximum)' },
];

// Preset avatar colors
const avatarColors = [
  '#5D5FEF', // Elevair purple
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#6B7280', // Gray
  '#000000', // Black
];

export default function ProfileSettingsPage() {
  const { user, organization, refreshUser } = useAuth();
  const updateProfile = useUpdateProfile();

  // Fetch fresh subscription data to ensure we always display the correct tier
  const { data: subscriptionData } = useSubscription();

  const [selectedColor, setSelectedColor] = useState(user?.profile?.avatarColor || '#5D5FEF');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.profile?.firstName || '',
      lastName: user?.profile?.lastName || '',
      phone: user?.profile?.phone || '',
      bio: user?.profile?.bio || '',
      sessionIdleTimeout: user?.profile?.sessionIdleTimeout || 30,
    },
  });

  // Reset form when user data loads
  useEffect(() => {
    if (user) {
      reset({
        firstName: user.profile?.firstName || '',
        lastName: user.profile?.lastName || '',
        phone: user.profile?.phone || '',
        bio: user.profile?.bio || '',
        sessionIdleTimeout: user.profile?.sessionIdleTimeout || 30,
      });
      setSelectedColor(user.profile?.avatarColor || '#5D5FEF');
    }
  }, [user, reset]);

  const onSubmit = async (data: ProfileFormData) => {
    setError(null);
    setSuccess(false);

    try {
      await updateProfile.mutateAsync({
        ...data,
        avatarColor: selectedColor,
      });

      await refreshUser();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to update profile');
    }
  };

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
  };

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const fullName = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim();
  // Use fresh subscription data, falling back to organization data
  const tier = (subscriptionData?.subscription?.tier || organization?.subscription?.tier || 'free') as SubscriptionTier;
  const userRole = user.role as UserRole;

  // Check if user has access to contracts (Rise+ tier)
  const currentTierIndex = TIER_HIERARCHY.indexOf(tier);
  const riseTierIndex = TIER_HIERARCHY.indexOf('rise');
  const canAccessContracts = currentTierIndex >= riseTierIndex;

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} mb={3}>
        Profile Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Profile updated successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Avatar Section */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <AvatarUpload
                name={fullName}
                currentAvatarUrl={user.profile?.avatar}
                avatarColor={selectedColor}
                tier={tier}
                userRole={userRole}
                onSuccess={refreshUser}
              />

              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                {user.email}
              </Typography>

              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mt={2}>
                <TierBadge tier={tier} userRole={userRole} size={20} />
                <Typography variant="body2" color="text.secondary">
                  {userRole === 'super_admin' ? 'Platform Master' : tierDisplayNames[tier] || 'Free Plan'}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Avatar Color Picker - only shows when no photo uploaded */}
              {!user.profile?.avatar && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Avatar Color
                  </Typography>
                  <Box display="flex" flexWrap="wrap" justifyContent="center" gap={1} mt={1}>
                    {avatarColors.map((color) => (
                      <IconButton
                        key={color}
                        onClick={() => handleColorChange(color)}
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: color,
                          border: selectedColor === color ? '3px solid' : '2px solid',
                          borderColor: selectedColor === color ? 'primary.main' : 'divider',
                          '&:hover': {
                            bgcolor: color,
                            opacity: 0.8,
                          },
                        }}
                      >
                        {selectedColor === color && (
                          <Check sx={{ color: 'white', fontSize: 18 }} />
                        )}
                      </IconButton>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Form */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="First Name"
                          fullWidth
                          required
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Last Name"
                          fullWidth
                          required
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      value={user.email}
                      fullWidth
                      disabled
                      helperText="Contact support to change your email"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Phone Number"
                          fullWidth
                          placeholder="123-456-7890"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="bio"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Bio"
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Tell us a bit about yourself or your business..."
                          helperText={`${field.value?.length || 0}/500`}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>
                  Session Settings
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="sessionIdleTimeout"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel id="session-timeout-label">Session Idle Timeout</InputLabel>
                          <Select
                            labelId="session-timeout-label"
                            id="session-timeout"
                            value={field.value || 30}
                            label="Session Idle Timeout"
                            onChange={(e) => field.onChange(e.target.value as number)}
                          >
                            {SESSION_TIMEOUT_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            You will be automatically logged out after this period of inactivity
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || (!isDirty && selectedColor === user.profile?.avatarColor)}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Change Password Section */}
      <ChangePasswordSection />

      {/* Business Hours & Timezone Section */}
      <BusinessHoursSection />

      {/* Integrations Section */}
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          Integrations
        </Typography>
        <GoogleCalendarConnect />
      </Box>

      {/* Contract Settings Section - Rise+ tier only */}
      {canAccessContracts && (
        <Box mt={4}>
          <ContractSettingsSection />
        </Box>
      )}
    </Box>
  );
}

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(8, 'New password must be at least 8 characters'),
  confirmPassword: z.string().min(1, 'Please confirm your new password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

function ChangePasswordSection() {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const generateStrongPassword = () => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghjkmnpqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%&*?';
    const all = upper + lower + digits + symbols;

    // Ensure at least one of each type
    let password = '';
    password += upper[Math.floor(Math.random() * upper.length)];
    password += lower[Math.floor(Math.random() * lower.length)];
    password += digits[Math.floor(Math.random() * digits.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    // Fill remaining 12 characters randomly
    for (let i = 0; i < 12; i++) {
      password += all[Math.floor(Math.random() * all.length)];
    }

    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');

    setValue('newPassword', password, { shouldValidate: true });
    setValue('confirmPassword', password, { shouldValidate: true });
    setShowNewPassword(true);
  };

  const onSubmit = async (data: PasswordFormData) => {
    setError(null);
    setSuccess(false);

    try {
      await api.patch('/users/me/password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });

      setSuccess(true);
      reset();
      setTimeout(() => setSuccess(false), 5000);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to change password');
    }
  };

  return (
    <Box mt={4}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Change Password
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password changed successfully!
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="currentPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Current Password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      fullWidth
                      error={!!errors.currentPassword}
                      helperText={errors.currentPassword?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              edge="end"
                              size="small"
                            >
                              {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<VpnKey />}
                  onClick={generateStrongPassword}
                  sx={{ mt: 1 }}
                >
                  Generate Strong Password
                </Button>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="newPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="New Password"
                      type={showNewPassword ? 'text' : 'password'}
                      fullWidth
                      error={!!errors.newPassword}
                      helperText={errors.newPassword?.message || 'Minimum 8 characters'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                              size="small"
                            >
                              {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Confirm New Password"
                      type={showNewPassword ? 'text' : 'password'}
                      fullWidth
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Changing...' : 'Change Password'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

// Common timezone options
const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Toronto', label: 'Toronto (ET)' },
  { value: 'America/Vancouver', label: 'Vancouver (PT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Generate time options in 30-min increments
const TIME_OPTIONS: { value: string; label: string }[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const label = `${displayHour}:${String(m).padStart(2, '0')} ${period}`;
    TIME_OPTIONS.push({ value, label });
  }
}

interface BusinessHour {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isEnabled: boolean;
}

const DEFAULT_HOURS: BusinessHour[] = [
  { dayOfWeek: 0, startTime: '09:00', endTime: '12:00', isEnabled: false },
  { dayOfWeek: 1, startTime: '09:00', endTime: '17:00', isEnabled: true },
  { dayOfWeek: 2, startTime: '09:00', endTime: '17:00', isEnabled: true },
  { dayOfWeek: 3, startTime: '09:00', endTime: '17:00', isEnabled: true },
  { dayOfWeek: 4, startTime: '09:00', endTime: '17:00', isEnabled: true },
  { dayOfWeek: 5, startTime: '09:00', endTime: '17:00', isEnabled: true },
  { dayOfWeek: 6, startTime: '09:00', endTime: '12:00', isEnabled: false },
];

function BusinessHoursSection() {
  const { organization, refreshUser } = useAuth();
  const updateSettings = useUpdateOrganizationSettings();

  const [timezone, setTimezone] = useState(organization?.settings?.timezone || 'America/New_York');
  const [hours, setHours] = useState<BusinessHour[]>(() => {
    const orgHours = organization?.settings?.businessHours;
    if (orgHours && orgHours.length === 7) return orgHours;
    return DEFAULT_HOURS;
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (organization?.settings) {
      setTimezone(organization.settings.timezone || 'America/New_York');
      if (organization.settings.businessHours?.length === 7) {
        setHours(organization.settings.businessHours);
      }
    }
  }, [organization]);

  const handleToggleDay = (dayOfWeek: number) => {
    setHours(prev => prev.map(h =>
      h.dayOfWeek === dayOfWeek ? { ...h, isEnabled: !h.isEnabled } : h
    ));
  };

  const handleTimeChange = (dayOfWeek: number, field: 'startTime' | 'endTime', value: string) => {
    setHours(prev => prev.map(h =>
      h.dayOfWeek === dayOfWeek ? { ...h, [field]: value } : h
    ));
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(false);
    try {
      await updateSettings.mutateAsync({ timezone, businessHours: hours });
      await refreshUser();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(e.response?.data?.error?.message || 'Failed to update settings');
    }
  };

  // Sort hours to display Monday first (1,2,3,4,5,6,0)
  const sortedHours = [...hours].sort((a, b) => {
    const order = [1, 2, 3, 4, 5, 6, 0];
    return order.indexOf(a.dayOfWeek) - order.indexOf(b.dayOfWeek);
  });

  return (
    <Box mt={4}>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Schedule color="action" />
            <Typography variant="h6">Business Hours</Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Business hours updated!
            </Alert>
          )}

          {/* Timezone */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Timezone</InputLabel>
            <Select
              value={timezone}
              label="Timezone"
              onChange={(e) => setTimezone(e.target.value)}
            >
              {TIMEZONE_OPTIONS.map(tz => (
                <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Business Hours Table */}
          <Box>
            {sortedHours.map((day) => (
              <Box
                key={day.dayOfWeek}
                display="flex"
                alignItems="center"
                gap={2}
                py={1.5}
                sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}
              >
                <Box width={120}>
                  <Typography
                    variant="body2"
                    fontWeight={day.isEnabled ? 600 : 400}
                    color={day.isEnabled ? 'text.primary' : 'text.disabled'}
                  >
                    {DAY_NAMES[day.dayOfWeek]}
                  </Typography>
                </Box>

                <Switch
                  checked={day.isEnabled}
                  onChange={() => handleToggleDay(day.dayOfWeek)}
                  size="small"
                />

                {day.isEnabled ? (
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={day.startTime}
                        onChange={(e) => handleTimeChange(day.dayOfWeek, 'startTime', e.target.value)}
                      >
                        {TIME_OPTIONS.map(t => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary">to</Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={day.endTime}
                        onChange={(e) => handleTimeChange(day.dayOfWeek, 'endTime', e.target.value)}
                      >
                        {TIME_OPTIONS.map(t => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.disabled" flex={1}>
                    Closed
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={updateSettings.isPending}
            >
              {updateSettings.isPending ? 'Saving...' : 'Save Business Hours'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
